/**
 * StepReview - Step 4: Review and Save
 *
 * Shows a summary of all entered information before finalizing.
 */

import {
  SnippetItem,
  SnippetDraftData,
  getSnippetTypeLabel,
  getTextTypeLabel,
  getImageSubtypeLabel,
} from "../../../api/snippets";
import { useMediaUrl } from "../../../hooks/useMediaUrl";

interface StepReviewProps {
  snippet: SnippetItem;
  draftData: SnippetDraftData;
  imageKey: number;
}

export function StepReview({ snippet, draftData, imageKey }: StepReviewProps) {
  // Image preview
  const { url: imageUrl, isLoading: imageLoading } = useMediaUrl(
    snippet.id,
    snippet.file_size + imageKey
  );

  // Format record subtype for display
  const formatRecordSubtype = (value: string | null): string => {
    if (!value) return "Not specified";
    // Convert snake_case to Title Case
    return value
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Image preview */}
      <div className="space-y-4">
        <div className="bg-vault-100 rounded-lg p-4 min-h-[300px] flex items-center justify-center">
          {imageLoading ? (
            <div className="flex flex-col items-center gap-2 text-vault-500">
              <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span>Loading image...</span>
            </div>
          ) : imageUrl ? (
            <img
              src={imageUrl}
              alt={snippet.original_filename}
              className="max-w-full max-h-[400px] object-contain rounded shadow-lg"
            />
          ) : (
            <div className="text-vault-400">No image available</div>
          )}
        </div>

        {/* File info */}
        <div className="bg-white border border-vault-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-vault-800 mb-2">File Information</h4>
          <dl className="space-y-1 text-sm">
            <div className="flex justify-between">
              <dt className="text-vault-500">Filename:</dt>
              <dd className="text-vault-900 font-medium">{snippet.original_filename}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-vault-500">Size:</dt>
              <dd className="text-vault-900">{(snippet.file_size / 1024).toFixed(1)} KB</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-vault-500">Type:</dt>
              <dd className="text-vault-900">{snippet.content_type}</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Summary */}
      <div className="space-y-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-green-600">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="font-semibold text-green-800">Ready to Save</h3>
          </div>
          <p className="text-green-700 text-sm">
            Review the details below and click &ldquo;Save to Library&rdquo; to complete.
          </p>
        </div>

        {/* Classification */}
        <div className="bg-white border border-vault-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-vault-800 mb-3 flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
            </svg>
            Classification
          </h4>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-vault-500">Type:</dt>
              <dd className="text-vault-900 font-medium">
                {getSnippetTypeLabel(draftData.snippet_type)}
              </dd>
            </div>
            {draftData.snippet_type === "image" && draftData.image_subtype && (
              <div className="flex justify-between">
                <dt className="text-vault-500">Image type:</dt>
                <dd className="text-vault-900">
                  {getImageSubtypeLabel(draftData.image_subtype)}
                </dd>
              </div>
            )}
            {draftData.snippet_type === "document" && (
              <>
                <div className="flex justify-between">
                  <dt className="text-vault-500">Text type:</dt>
                  <dd className="text-vault-900">
                    {getTextTypeLabel(draftData.text_type)}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-vault-500">Record type:</dt>
                  <dd className="text-vault-900">
                    {formatRecordSubtype(draftData.record_subtype)}
                  </dd>
                </div>
              </>
            )}
          </dl>
        </div>

        {/* Metadata */}
        <div className="bg-white border border-vault-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-vault-800 mb-3 flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
            </svg>
            Details
          </h4>
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-vault-500 mb-1">Title:</dt>
              <dd className="text-vault-900">
                {draftData.caption || (
                  <span className="text-vault-400 italic">No title provided</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-vault-500 mb-1">Description:</dt>
              <dd className="text-vault-900">
                {draftData.description || (
                  <span className="text-vault-400 italic">No description provided</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-vault-500 mb-1">Date:</dt>
              <dd className="text-vault-900">
                {draftData.estimated_date_text || (
                  <span className="text-vault-400 italic">No date provided</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-vault-500 mb-1">Location:</dt>
              <dd className="text-vault-900">
                {draftData.location || (
                  <span className="text-vault-400 italic">No location provided</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-vault-500 mb-1">Tagged People:</dt>
              <dd className="text-vault-900">
                {(draftData.person_ids?.length || 0) + (draftData.connected_person_ids?.length || 0) > 0 ? (
                  <span>
                    {draftData.person_ids?.length || 0} family member(s), {draftData.connected_person_ids?.length || 0} connected person(s)
                  </span>
                ) : (
                  <span className="text-vault-400 italic">No people tagged</span>
                )}
              </dd>
            </div>
          </dl>
        </div>

        {/* Transcription (if available) */}
        {snippet.transcription && (
          <div className="bg-white border border-vault-200 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-vault-800 mb-3 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
              Extracted Text
            </h4>
            <div className="bg-vault-50 rounded p-3 max-h-40 overflow-y-auto">
              <p className="text-sm text-vault-800 whitespace-pre-wrap font-mono">
                {snippet.transcription}
              </p>
            </div>
            <p className="text-xs text-vault-500 mt-2">
              {snippet.transcription.length} characters extracted via OCR
            </p>
          </div>
        )}

        {/* Image preparation status */}
        <div className="bg-white border border-vault-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-vault-800 mb-3 flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
            </svg>
            Image Preparation
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              {draftData.rotation ? (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-green-600">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                  <span className="text-vault-900">Rotated {draftData.rotation}°</span>
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-vault-400">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span className="text-vault-500">No rotation applied</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              {draftData.crop_applied ? (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-green-600">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                  <span className="text-vault-900">Image cropped</span>
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-vault-400">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span className="text-vault-500">No cropping applied</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              {draftData.enhancements ? (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-green-600">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                  <span className="text-vault-900">
                    Enhancements applied
                    {draftData.enhancements.grayscale && " (grayscale)"}
                  </span>
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4 text-vault-400">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span className="text-vault-500">No enhancements applied</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StepReview;
